import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { EmptyState, SectionCard } from "./shared";
import { formatCurrency, formatDateDMY } from "./LineGrid";
import { ACCOUNTING_BASE } from "./accountingNav";
import VendorBillEditorForm from "./VendorBillEditorForm";

/**
 * Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill). This page used to
 * be a thin wrapper around the shared raw Debit/Credit grid
 * (GridVoucherFormPage) - it now hosts the dedicated VendorBillEditorForm
 * (header + Vendor + auto-built accounting) plus a "Recent Vendor Bills"
 * list, reusing the Phase 15 vendor-bills Outstanding report endpoint so
 * Paid/Outstanding here can never disagree with the dedicated Outstanding
 * report page (Vendors -> Vendor Bills Outstanding).
 */
export default function VendorBillsPage() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/vendor-bills");
      setBills((res.data?.rows || []).slice(0, 15));
    } catch (err) {
      toast.error(formatErr(err, "Could not load recent vendor bills"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteBill = async (row) => {
    if (!window.confirm(`Delete Vendor Bill ${row.billNo}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${row.billVoucherId}`);
      toast.success("Vendor Bill deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this Vendor Bill"));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Purchase / Vendor Bill</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Book a vendor bill on credit - pick a Vendor, add one or more Expense/Asset rows, and save. The accounting entry (Dr Expense/Asset, Cr Vendor Payable) is always built automatically.</p>
      </div>

      <VendorBillEditorForm mode="create" onSaved={load} />

      <SectionCard title="Recent Vendor Bills">
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Vendor</TableHead>
                <TableHead>Vendor Invoice No.</TableHead><TableHead className="text-right">Bill Amount</TableHead>
                <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Due Date</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : bills.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="py-0"><div className="py-8"><EmptyState>No vendor bills yet. Create your first vendor bill above.</EmptyState></div></TableCell></TableRow>
              ) : bills.map((row) => (
                <TableRow key={row.billVoucherId}>
                  <TableCell>{formatDateDMY(row.billDate)}</TableCell>
                  <TableCell className="font-mono text-xs font-semibold">{row.billNo}</TableCell>
                  <TableCell>{row.vendorName || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{row.billNo}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.originalAmount)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.paid)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(row.balance)}</TableCell>
                  <TableCell>{row.dueDate ? formatDateDMY(row.dueDate) : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${row.billVoucherId}`} className="text-[#111827] hover:underline">View</Link>
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${row.billVoucherId}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${row.billVoucherId}?print=1`} className="text-[#111827] hover:underline">Print</Link>
                      <button type="button" onClick={() => deleteBill(row)} className="text-red-600 hover:underline">Delete</button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          Showing the 15 most recent bills. For search/filter across every bill, use Vendors &rarr; Vendor Bills Outstanding.
        </p>
      </SectionCard>
    </div>
  );
}
